import React, { useState, useEffect, useRef } from 'react';
import { workOrdersApi } from '../../api/workOrdersApi';
import { useSignalR } from '../../hooks/useSignalR';
import { useAuthStore } from '../../store/authStore';
import { useNotificationSound } from '../../hooks/useNotificationSound';
import { ChatThread, type ChatMessage, type TypingIndicator } from '../chat/ChatThread';

type TimeoutHandle = ReturnType<typeof setTimeout>;

interface ChatPanelProps {
  workOrderId: number;
  vehicleInfo: string;
  onClose: () => void;
}

const TECH_QUICK_REPLIES = [
  '👍 Almost done',
  '🔧 Working on it',
  '⏳ Running behind',
  '🔍 Checking again',
  '✅ Ready for inspection',
  '❓ Need clarification',
];

export const ChatPanel: React.FC<ChatPanelProps> = ({
  workOrderId,
  vehicleInfo,
  onClose
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [isTyping, setIsTyping] = useState<TypingIndicator | null>(null);
  const seenIdsRef = useRef(new Set<number>());
  const typingTimeoutRef = useRef<TimeoutHandle>();
  const typingEmitRef = useRef<TimeoutHandle>();
  const { on, off, invoke } = useSignalR();
  const staffId = useAuthStore((s) => s.staffId);
  const staffName = useAuthStore((s) => s.staffName) ?? 'Technician';
  const { soundEnabled, toggleSound, playNotificationSound } = useNotificationSound();

  useEffect(() => {
    loadMessages();
    const pollId = window.setInterval(() => void loadMessages(true), 5_000);
    try { setMessageText(localStorage.getItem(`wrenchlab:chat-draft:${workOrderId}`) ?? ''); } catch { /* storage unavailable */ }
    return () => window.clearInterval(pollId);
  }, [workOrderId]);

  useEffect(() => {
    const handleNewChatMessage = (...args: unknown[]) => {
      const receivedWorkOrderId = args[0] as number;
      const messageData = args[1] as ChatMessage;
      if (receivedWorkOrderId !== workOrderId) return;
      if (seenIdsRef.current.has(messageData.chatMessageId)) return;
      seenIdsRef.current.add(messageData.chatMessageId);
      setMessages(prev => [...prev, messageData]);
      setIsTyping(null);
      // The technician shell owns the single global chime. Keeping audio there
      // prevents duplicate sounds when this conversation is already open.
    };

    const handleUserTyping = (...args: unknown[]) => {
      const receivedWorkOrderId = args[0] as number;
      const typingData = args[1] as { senderName: string } | null;
      if (receivedWorkOrderId !== workOrderId || !typingData) return;
      setIsTyping({ senderName: typingData.senderName });
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setIsTyping(null), 3000);
    };

    on('NewChatMessage', handleNewChatMessage);
    on('UserTyping', handleUserTyping);
    return () => {
      off('NewChatMessage', handleNewChatMessage);
      off('UserTyping', handleUserTyping);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [workOrderId, staffId, on, off]);

  const loadMessages = async (background = false) => {
    try {
      if (!background) setLoading(true);
      const data: ChatMessage[] = await workOrdersApi.getWorkOrderChat(workOrderId);
      seenIdsRef.current = new Set(data.map((m) => m.chatMessageId));
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      if (!background) setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    setSending(true);
    setSendError('');
    try {
      const newMessage: ChatMessage = await workOrdersApi.sendWorkOrderChatMessage(workOrderId, {
        messageText: messageText.trim()
      });
      if (!seenIdsRef.current.has(newMessage.chatMessageId)) {
        seenIdsRef.current.add(newMessage.chatMessageId);
        setMessages(prev => [...prev, newMessage]);
      }
      setMessageText('');
      try { localStorage.removeItem(`wrenchlab:chat-draft:${workOrderId}`); } catch { /* storage unavailable */ }
    } catch (error) {
      console.error('Failed to send message:', error);
      setSendError('Message not sent. Your draft is saved—check your connection and retry.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end bg-slate-950/55 backdrop-blur-sm md:items-stretch md:justify-end" role="dialog" aria-modal="true" aria-label={`Work order chat for ${vehicleInfo}`}>
      <div className="flex max-h-[88vh] w-full flex-col rounded-t-3xl border border-slate-200 bg-white shadow-2xl animate-slide-up md:h-full md:max-h-none md:w-[440px] md:rounded-none md:border-y-0 md:border-r-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-[#0b2039] p-5 text-white">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-5 h-5 text-secondary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H6.5c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99z" />
              </svg>
              <p className="text-base font-bold">{vehicleInfo}</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-status-success rounded-full" />
              <p className="text-xs text-slate-300">Work-order conversation</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { const enabling = !soundEnabled; toggleSound(); if (enabling) void playNotificationSound(true); }}
              className={`rounded-lg p-2 transition-colors ${soundEnabled ? 'text-orange-400 hover:bg-white/10' : 'text-slate-400 hover:bg-white/10'}`}
              title={soundEnabled ? 'Sound enabled' : 'Sound disabled'}
            >
              {soundEnabled ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16.6915026,12.4744748 L16.6915026,6.45258205 C16.6915026,4.51328067 15.1820742,2.99337217 13.2088889,2.99337217 C11.2356707,2.99337217 9.72624874,4.51328067 9.72624874,6.45258205 L9.72624874,12.4744748 C9.72624874,14.4136378 11.2356707,15.9335807 13.2088889,15.9335807 C15.1820742,15.9335807 16.6915026,14.4136378 16.6915026,12.4744748 Z M21.527211,12.4744748 C21.527211,12.4744748 21.527211,15.0151496 21.527211,15.4744748 L21.527211,20.0151496 C21.527211,21.6536872 20.1706324,23 18.518788,23 L7.49012012,23 C5.83827599,23 4.48179747,21.6536872 4.48179747,20.0151496 L4.48179747,15.4744748 L2.58923381,15.4744748 C1.31301674,15.4744748 0.317098571,14.4744748 0.317098571,13.1744748 C0.317098571,11.8744748 1.31301674,10.8744748 2.58923381,10.8744748 L4.48179747,10.8744748 L4.48179747,6.45258205 C4.48179747,2.86779827 7.39647773,0 11.0650301,0 C14.7335826,0 17.647268,2.86779827 17.647268,6.45258205 L17.647268,10.8744748 L19.5397317,10.8744748 C20.8159488,10.8744748 21.811864,11.8744748 21.811864,13.1744748 C21.811864,14.4744748 20.8159488,15.4744748 19.5397317,15.4744748 L17.647268,15.4744748 L17.647268,15.4744748 C17.647268,16.0425447 17.6467764,16.5425447 17.6467764,16.7325447 L17.6467764,20.0151496 C17.6467764,21.6536872 16.2901978,23 14.6384536,23 L7.49012012,23 C5.83827599,23 4.48179747,21.6536872 4.48179747,20.0151496 L4.48179747,16.7325447 C4.48179747,16.5425447 4.48135826,16.0425447 4.48135826,15.4744748 L4.48135826,15.4744748" />
                </svg>
              )}
            </button>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages — using shared ChatThread */}
        <div className="flex-1 overflow-y-auto bg-surface-light">
          <ChatThread
            messages={messages}
            currentStaffId={staffId}
            loading={loading}
            emptyText="No messages yet. Start the conversation!"
            isTyping={isTyping}
          />
        </div>

        {/* Input */}
        {sendError && <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-xs font-medium text-red-700">{sendError}</div>}
        <form onSubmit={handleSendMessage} className="flex gap-2 border-t border-slate-200 bg-white p-4">
          <input
            type="text"
            value={messageText}
            onChange={e => {
              setMessageText(e.target.value);
              try { localStorage.setItem(`wrenchlab:chat-draft:${workOrderId}`, e.target.value); } catch { /* storage unavailable */ }
              if (e.target.value.trim()) {
                if (!typingEmitRef.current) {
                  invoke('NotifyTyping', workOrderId, staffName).catch(() => {});
                }
                clearTimeout(typingEmitRef.current);
                typingEmitRef.current = setTimeout(() => {
                  typingEmitRef.current = undefined;
                }, 2000);
              }
            }}
            placeholder="Type a message..."
            disabled={sending}
            className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm outline-none transition-colors focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!messageText.trim() || sending}
            className="rounded-xl bg-orange-500 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-50"
          >
            {sending ? '...' : 'Send'}
          </button>
        </form>

        {/* Quick replies */}
        {!messageText.trim() && (
          <div className="px-4 py-2 bg-surface-container border-t border-border-subtle flex flex-wrap gap-2">
            {TECH_QUICK_REPLIES.map((reply) => (
              <button
                key={reply}
                onClick={() => setMessageText(reply)}
                className="text-xs px-2 py-1 bg-surface-container-lowest border border-border-subtle rounded-full hover:bg-surface-container transition-colors text-secondary whitespace-nowrap"
              >
                {reply}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
